import { useCallback, useEffect, useState } from "react";
import { modalGateBtnClass } from "./ModalValHint";

/**
 * Shared gated-primary-CTA pattern: greyed but clickable, surfaces a nudge on
 * click when validation fails, clears when `autoClearWhen` flips true.
 */
export function useGatedAction({
  canProceed,
  busy = false,
  validate,
  onProceed,
  autoClearWhen
}: {
  canProceed: boolean;
  busy?: boolean;
  /** Return a nudge message when the action should not run. */
  validate?: () => string | null;
  onProceed: () => void | Promise<void>;
  /** When true, any visible nudge is cleared (defaults to `canProceed`). */
  autoClearWhen?: boolean;
}) {
  const [nudge, setNudge] = useState<string | null>(null);
  const shouldClear = autoClearWhen ?? canProceed;

  useEffect(() => {
    if (shouldClear) setNudge(null);
  }, [shouldClear]);

  const clearNudge = useCallback(() => setNudge(null), []);

  const handleClick = useCallback(() => {
    if (busy) return;
    if (validate) {
      const msg = validate();
      if (msg) {
        setNudge(msg);
        return;
      }
    } else if (!canProceed) {
      return;
    }
    setNudge(null);
    void onProceed();
  }, [busy, validate, canProceed, onProceed]);

  return {
    nudge,
    handleClick,
    clearNudge,
    gateBtnClass: modalGateBtnClass(canProceed, busy),
    ariaDisabled: !canProceed || busy,
    // A truly non-interactive state: the action is in flight. Callers wire this
    // to the DOM `disabled` so an in-flight primary can't be re-clicked (no
    // double-submit) or activated by keyboard/AT. The greyed-but-clickable
    // *validation* gate stays enabled so a click can still surface the nudge.
    disabled: busy
  };
}
